import React, { useCallback, useRef, useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { useDismiss } from '../hooks/useDismiss';
import { DropdownOption, DropdownPanel } from './DropdownPanel';
type SelectFieldProps = {
  id: string;
  step?: string;
  label: string;
  // Lufthansa-style: label kept for screen readers only, heavier underline
  hideLabel?: boolean;
  // Lufthansa detail: the underline turns up in a short tick at its right end
  endTick?: boolean;
  value: string;
  placeholder: string;
  options: string[];
  onChange: (value: string) => void;
  invalid?: boolean;
  describedBy?: string;
};

// Underlined single-choice field with a custom styled list
export function SelectField({
  id,
  step,
  label,
  hideLabel,
  endTick,
  value,
  placeholder,
  options,
  onChange,
  invalid,
  describedBy
}: SelectFieldProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(rootRef, open, close);
  return <div ref={rootRef} className="relative" data-id="element-1104" data-mp="2yfh7cp7qytrz">
      <span id={`${id}-label`} className={hideLabel ? 'sr-only' : 'block text-[13px] font-light text-ink/75'} data-id="element-1105" data-mp="336z6r8u4gmau">
        {step && <span className="mr-1.5 text-ink/50" data-id="element-1106" data-mp="2pg46gkxovdiv">{step}</span>}
        {label}
      </span>
      <button type="button" id={id} onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} aria-labelledby={`${id}-label ${id}`} aria-invalid={invalid} aria-describedby={describedBy} className={`relative w-full truncate ${hideLabel ? 'border-b-2' : 'border-b'} ${endTick ? 'after:absolute after:-bottom-[2px] after:right-0 after:h-[11px] after:w-[2px] after:bg-ink' : ''} border-ink bg-transparent pb-3 pr-8 pt-1.5 text-left text-[18px] focus:outline-none focus-visible:[box-shadow:0_1px_0_0_#000] ${value ? 'text-ink' : 'font-light text-ink/60'}`} data-id="element-1107" data-mp="2uefh2xvt5s25">
        {value || placeholder}
        <ChevronDownIcon className={`pointer-events-none absolute ${endTick ? 'right-3' : 'right-0'} top-2.5 h-5 w-5 transition-transform duration-300 ease-luxe ${open ? 'rotate-180' : ''}`} strokeWidth={1.25} aria-hidden="true" data-id="element-1108" data-mp="1ho5blyzyfy2m" />
      </button>
      <DropdownPanel open={open} data-id="element-1109" data-mp="13x0fv7zpjpxk">
        <ul role="listbox" aria-labelledby={`${id}-label`} className="max-h-[320px] overflow-y-auto py-2" data-id="element-1110" data-mp="13vpa52mpc83t">
          {options.map(o => <DropdownOption key={o} label={o} selected={o === value} onSelect={() => {
          onChange(o);
          setOpen(false);
        }} data-id="element-1111" data-mp="tyax7czzm1g9" />)}
        </ul>
      </DropdownPanel>
    </div>;
}